/**
 * `program.kind` vocabulary — EXACT mirror of the migration's CHECK
 * (`00055_academy_programs.sql`, extended by `00090_free_tier_and_funnel.sql`).
 *
 * It used to be repeated in three places (the mutation, the programs view,
 * and the client schema comment), so adding `workshop` required touching all
 * three with nothing failing if you forgot one. Here it's a single list.
 *
 * `workshop` (B5 · gap analysis §N, action `N2`): a 1–2 standalone-session
 * workshop. It's not `free_course` — which is a complete free-standing
 * course — nor a three-month career: it's the HOOK format, and the catalog
 * needs to distinguish it so the "Free" lane doesn't mix a three-hour
 * workshop with a giveaway career.
 */

export const PROGRAM_KINDS = ["career", "diploma", "workshop", "free_course"] as const;
export type ProgramKind = (typeof PROGRAM_KINDS)[number];

/** Default kind for a new program (the least committal). */
export const DEFAULT_PROGRAM_KIND: ProgramKind = "free_course";

/**
 * Normalizes whatever comes from a `<select>` or the database to the vocabulary.
 *
 * The narrowing is done with a type guard and not with `as`: an `as` here
 * would silently accept any `kind` the database had extra (or missing)
 * after a migration, which is exactly the case this function exists to
 * catch.
 */
function isProgramKind(value: string): value is ProgramKind {
  return (PROGRAM_KINDS as readonly string[]).includes(value);
}

export function asProgramKind(value: string | null | undefined): ProgramKind {
  return value != null && isProgramKind(value) ? value : DEFAULT_PROGRAM_KIND;
}

/**
 * Is this program kind a WORKSHOP? Consumed by the catalog and the landing
 * page to label the "Free" lane.
 */
export function isWorkshop(kind: string | null | undefined): boolean {
  return kind === "workshop";
}
